"use client";
import "./card_slider.scss";
import { ReactElement, useState } from "react";
import classnames from "classnames";
import { Icon } from "@/elements/icon/icon";
import { i18n } from "../../model/i18n";
import { Text } from "../../elements/text/text";

interface Props {
  children?: ReactElement | ReactElement[];
  nextLabel?: string;
  noActive?: boolean;
  onChange?: (value: number) => void;
  previousLabel?: string;
  value?: number;
}

export function CardSlider({
  children,
  nextLabel,
  noActive,
  onChange,
  previousLabel,
  value,
}: Props): ReactElement {
  const items = Array.isArray(children) ? children : [children];
  const itemsCount = items.length;
  const [active, setActive] = useState(0);

  const activeIndex = value ?? active;

  function setActiveIndex(index: number): void {
    if (onChange) {
      return onChange(index);
    }
    setActive(index);
  }

  const disabledLeft = activeIndex === 0;
  const disabledRight = activeIndex === itemsCount - 1;

  const arrowLeft = (
    <button
      className={classnames(
        "card-slider__button",
        "card-slider__button--left",
        {
          "card-slider__button--disabled": disabledLeft,
          "card-slider__button--with-label": previousLabel,
        },
      )}
      onClick={() => {
        setActiveIndex(Math.max(activeIndex - 1, 0));
      }}
      aria-label={i18n.t("components.card_slider.arrow_left")}
      disabled={disabledLeft}
    >
      <Icon
        name="arrow-slider-left"
        size={42}
        color={disabledLeft ? "disabled-on-default" : "accent-tinted"}
      />
      {!disabledLeft && previousLabel && (
        <span className="card-slider__button-label">
          <Text type="body-xbold" color="link-default" hoverColor="link-hover">
            {previousLabel}
          </Text>
        </span>
      )}
    </button>
  );
  const arrowRight = (
    <button
      className={classnames(
        "card-slider__button",
        "card-slider__button--right",
        {
          "card-slider__button--disabled": disabledRight,
          "card-slider__button--with-label": nextLabel,
        },
      )}
      onClick={() => {
        setActiveIndex(Math.min(activeIndex + 1, itemsCount - 1));
      }}
      aria-label={i18n.t("components.card_slider.arrow_right")}
      disabled={disabledRight}
    >
      <Icon
        name="arrow-slider-right"
        size={42}
        color={disabledRight ? "disabled-on-default" : "accent-tinted"}
      />
      {!disabledRight && nextLabel && (
        <span className="card-slider__button-label">
          <Text type="body-xbold" color="link-default" hoverColor="link-hover">
            {nextLabel}
          </Text>
        </span>
      )}
    </button>
  );

  return (
    <div
      className={classnames("card-slider", {
        "card-slider--no-active": noActive,
        "card-slider--with-labels": previousLabel || nextLabel,
      })}
    >
      <div className="card-slider__grid">
        {items.map((item, index) => {
          const isBefore = index < activeIndex - 1;
          const isPrevious = index === activeIndex - 1;
          const isCurrent = index === activeIndex;
          const isNext = index === activeIndex + 1;
          const isAfter = index > activeIndex + 1;

          return (
            <div className="card-slider__wrapper" key={index}>
              <div
                className={classnames("card-slider__item", {
                  "card-slider__item--previous": isPrevious,
                  "card-slider__item--current": isCurrent,
                  "card-slider__item--next": isNext,
                  "card-slider__item--before": isBefore,
                  "card-slider__item--after": isAfter,
                })}
                onClick={() => setActiveIndex(index)}
              >
                {item}
              </div>
            </div>
          );
        })}
        {itemsCount > 1 && (
          <footer className="card-slider__controls">
            <div className="card-slider__controls-content">
              {arrowLeft}
              {arrowRight}
            </div>
          </footer>
        )}
      </div>
    </div>
  );
}
